import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Box, ShoppingCart, BarChart3, AlertTriangle, CheckCircle2, Package, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const TYPES = ["IOL", "Capsular Tension Ring", "Trabecular Stent", "KPro", "Glaucoma Implant", "Scleral Buckle", "Other"];
const STOCK_TONE = {
  healthy: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Healthy" },
  low: { bg: "bg-amber-100", text: "text-amber-700", label: "Low stock" },
  expiring_soon: { bg: "bg-orange-100", text: "text-orange-700", label: "Expiring ≤ 7d" },
  out_of_stock: { bg: "bg-rose-100", text: "text-rose-700", label: "Out of stock" },
  expired: { bg: "bg-rose-200", text: "text-rose-900", label: "Expired" },
};

export default function OtImplants() {
  const [tab, setTab] = useState("catalogue");
  const [implants, setImplants] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [stock, setStock] = useState({ items: [], summary: {} });
  const [report, setReport] = useState({ usage: [], purchases: [], pending: [], summary: {} });

  const [openImplant, setOpenImplant] = useState(false);
  const [editingImplant, setEditingImplant] = useState(null);  // null = create mode, else = edit mode
  const [openPurchase, setOpenPurchase] = useState(false);
  const [iForm, setIForm] = useState({ name: "", model: "", manufacturer: "", type: "IOL", default_power: "", mrp: 0, active: true });
  const [pForm, setPForm] = useState({ implant_id: "", batch_no: "", lot_no: "", expiry: "", quantity: 1, unit_cost: 0, supplier: "", invoice_no: "", received_date: "" });
  const [reportRange, setReportRange] = useState({ from_date: "", to_date: "" });

  const loadAll = async () => {
    const [a, b, c, d] = await Promise.all([
      api.get("/ot/implants"),
      api.get("/ot/implants/purchases"),
      api.get("/ot/implants/stock"),
      api.get("/ot/implants/report"),
    ]);
    setImplants(a.data.items);
    setPurchases(b.data.items);
    setStock(c.data);
    setReport(d.data);
  };
  useEffect(() => { loadAll(); }, []);

  const saveImplant = async () => {
    if (!iForm.name) { toast.error("Implant name is required"); return; }
    try {
      const payload = { ...iForm, mrp: parseFloat(iForm.mrp) || 0 };
      if (editingImplant) {
        await api.patch(`/ot/implants/${editingImplant.id}`, payload);
        toast.success("Implant updated");
      } else {
        await api.post("/ot/implants", payload);
        toast.success("Implant added to catalogue");
      }
      setOpenImplant(false);
      setEditingImplant(null);
      setIForm({ name: "", model: "", manufacturer: "", type: "IOL", default_power: "", mrp: 0, active: true });
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const startEdit = (im) => {
    setEditingImplant(im);
    setIForm({
      name: im.name || "",
      model: im.model || "",
      manufacturer: im.manufacturer || "",
      type: im.type || "IOL",
      default_power: im.default_power || "",
      mrp: im.mrp || 0,
      active: im.active !== false,
    });
    setOpenImplant(true);
  };

  const startCreate = () => {
    setEditingImplant(null);
    setIForm({ name: "", model: "", manufacturer: "", type: "IOL", default_power: "", mrp: 0, active: true });
    setOpenImplant(true);
  };

  const deleteImplant = async (im) => {
    try {
      await api.delete(`/ot/implants/${im.id}`);
      toast.success(`Deleted “${im.name}”`);
      loadAll();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (e?.response?.status === 409) {
        toast.error(detail, { duration: 7000 });
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    }
  };

  const savePurchase = async () => {
    if (!pForm.implant_id || !pForm.batch_no || !pForm.expiry || !pForm.quantity) {
      toast.error("Implant, batch, expiry & quantity are required"); return;
    }
    try {
      await api.post("/ot/implants/purchases", { ...pForm, quantity: parseInt(pForm.quantity, 10), unit_cost: parseFloat(pForm.unit_cost) || 0 });
      toast.success("Purchase recorded — stock updated");
      setOpenPurchase(false);
      setPForm({ implant_id: "", batch_no: "", lot_no: "", expiry: "", quantity: 1, unit_cost: 0, supplier: "", invoice_no: "", received_date: "" });
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const toggleActive = async (im) => {
    try { await api.patch(`/ot/implants/${im.id}`, { active: !im.active }); toast.success(im.active ? "Marked discontinued" : "Reactivated"); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const runReport = async () => {
    const params = new URLSearchParams();
    if (reportRange.from_date) params.set("from_date", reportRange.from_date);
    if (reportRange.to_date) params.set("to_date", reportRange.to_date);
    const { data } = await api.get(`/ot/implants/report?${params.toString()}`);
    setReport(data);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ot-implants-page">
      <div>
        <div className="section-label">Operation theatre · Implant register</div>
        <h1 className="font-heading text-3xl font-semibold">Implants &amp; IOL stock</h1>
        <p className="mt-1 text-sm text-ink-muted">NABH-compliant traceability · Catalogue · Purchases · Live stock · Usage report linked to OT.</p>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-ink-muted">On-hand units</div><div className="font-heading text-2xl font-bold text-brand">{stock.summary?.on_hand_units || 0}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-ink-muted">Healthy lines</div><div className="font-heading text-2xl font-bold text-emerald-700">{stock.summary?.healthy || 0}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-ink-muted">Low stock</div><div className="font-heading text-2xl font-bold text-amber-700">{stock.summary?.low || 0}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-ink-muted">Expiring ≤ 7d</div><div className="font-heading text-2xl font-bold text-orange-700">{stock.summary?.expiring_soon || 0}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-ink-muted">Out / expired</div><div className="font-heading text-2xl font-bold text-rose-700">{(stock.summary?.out_of_stock || 0) + (stock.summary?.expired || 0)}</div></Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="catalogue" data-testid="implants-tab-catalogue"><Box size={14} className="mr-1.5" />Catalogue ({implants.length})</TabsTrigger>
          <TabsTrigger value="purchases" data-testid="implants-tab-purchases"><ShoppingCart size={14} className="mr-1.5" />Purchases ({purchases.length})</TabsTrigger>
          <TabsTrigger value="stock" data-testid="implants-tab-stock"><Package size={14} className="mr-1.5" />Stock ({stock.summary?.total_lines || 0})</TabsTrigger>
          <TabsTrigger value="report" data-testid="implants-tab-report"><BarChart3 size={14} className="mr-1.5" />Report</TabsTrigger>
        </TabsList>

        {/* === Catalogue === */}
        <TabsContent value="catalogue" className="mt-4">
          <Card className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wider text-brand">Catalogue</div>
              <Button size="sm" className="bg-brand hover:bg-brand-700" data-testid="implant-new-btn" onClick={startCreate}><Plus size={12} className="mr-1" />Add implant</Button>
            </div>
            <Dialog open={openImplant} onOpenChange={(o) => { setOpenImplant(o); if (!o) setEditingImplant(null); }}>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle className="font-heading">{editingImplant ? "Edit implant" : "Add implant to catalogue"}</DialogTitle></DialogHeader>
                <div className="form-grid">
                  <div className="md:col-span-2"><Label>Name *</Label><Input value={iForm.name} onChange={(e) => setIForm({ ...iForm, name: e.target.value })} placeholder="e.g. Acrysof IQ Aspheric" data-testid="implant-name" /></div>
                  <div><Label>Type</Label>
                    <Select value={iForm.type} onValueChange={(v) => setIForm({ ...iForm, type: v })}>
                      <SelectTrigger data-testid="implant-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Model</Label><Input value={iForm.model} onChange={(e) => setIForm({ ...iForm, model: e.target.value })} placeholder="e.g. SN60WF" data-testid="implant-model" /></div>
                  <div><Label>Manufacturer</Label><Input value={iForm.manufacturer} onChange={(e) => setIForm({ ...iForm, manufacturer: e.target.value })} placeholder="e.g. Alcon" /></div>
                  <div><Label>Default power</Label><Input value={iForm.default_power} onChange={(e) => setIForm({ ...iForm, default_power: e.target.value })} placeholder="e.g. 21.0 D" /></div>
                  <div><Label>MRP (₹)</Label><Input type="number" value={iForm.mrp} onChange={(e) => setIForm({ ...iForm, mrp: e.target.value })} /></div>
                </div>
                <Button onClick={saveImplant} className="mt-3 bg-brand hover:bg-brand-700" data-testid="implant-save-btn">{editingImplant ? "Update" : "Save"}</Button>
              </DialogContent>
            </Dialog>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
                  <tr><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Model</th><th className="px-3 py-2 text-left">Type</th><th className="px-3 py-2 text-left">Manufacturer</th><th className="px-3 py-2 text-right">MRP</th><th className="px-3 py-2 text-right">Actions</th></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {implants.length === 0 && <tr><td colSpan="6" className="px-3 py-8 text-center text-ink-muted">No implants in catalogue yet. Click <b>Add implant</b> to start.</td></tr>}
                  {implants.map((im) => (
                    <tr key={im.id} className={im.active ? "" : "opacity-50"} data-testid={`implant-row-${im.id}`}>
                      <td className="px-3 py-2 font-medium">{im.name}{im.active === false && <Badge variant="outline" className="ml-2 border-rose-200 bg-rose-50 text-[10px] text-rose-700">Discontinued</Badge>}</td>
                      <td className="px-3 py-2 font-mono text-xs">{im.model || "—"}</td>
                      <td className="px-3 py-2"><Badge variant="outline">{im.type}</Badge></td>
                      <td className="px-3 py-2 text-xs text-ink-muted">{im.manufacturer || "—"}</td>
                      <td className="px-3 py-2 text-right">₹{(im.mrp || 0).toLocaleString("en-IN")}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => startEdit(im)} title="Edit" data-testid={`implant-edit-${im.id}`}>
                            <Pencil size={14} className="text-brand" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => toggleActive(im)} title={im.active ? "Discontinue (hide from pickers)" : "Reactivate"} data-testid={`implant-toggle-${im.id}`}>
                            <span className={`text-[10px] font-semibold uppercase tracking-wider ${im.active ? "text-amber-700" : "text-emerald-700"}`}>{im.active ? "Discontinue" : "Reactivate"}</span>
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="ghost" className="hover:bg-rose-50" title="Delete (hard delete — only if never purchased/used)" data-testid={`implant-delete-${im.id}`}>
                                <Trash2 size={14} className="text-rose-600" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete &ldquo;{im.name}&rdquo;?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This permanently removes the implant from your catalogue. If it has any purchase or OT usage records, deletion will be refused — use <b>Discontinue</b> instead to preserve the audit trail.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel data-testid={`implant-delete-cancel-${im.id}`}>Cancel</AlertDialogCancel>
                                <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => deleteImplant(im)} data-testid={`implant-delete-confirm-${im.id}`}>Delete</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* === Purchases === */}
        <TabsContent value="purchases" className="mt-4">
          <Card className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wider text-brand">Purchases (received stock)</div>
              <Dialog open={openPurchase} onOpenChange={setOpenPurchase}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-brand hover:bg-brand-700" data-testid="purchase-new-btn" disabled={implants.length === 0}><Plus size={12} className="mr-1" />Record purchase</Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader><DialogTitle className="font-heading">Record implant purchase</DialogTitle></DialogHeader>
                  <div className="form-grid">
                    <div className="md:col-span-2"><Label>Implant *</Label>
                      <Select value={pForm.implant_id} onValueChange={(v) => setPForm({ ...pForm, implant_id: v })}>
                        <SelectTrigger data-testid="purchase-implant"><SelectValue placeholder="Select from catalogue" /></SelectTrigger>
                        <SelectContent>{implants.filter((i) => i.active !== false).map((i) => <SelectItem key={i.id} value={i.id}>{i.name}{i.model ? ` · ${i.model}` : ""}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Batch no *</Label><Input value={pForm.batch_no} onChange={(e) => setPForm({ ...pForm, batch_no: e.target.value })} placeholder="e.g. 240612-A" data-testid="purchase-batch" /></div>
                    <div><Label>Lot no</Label><Input value={pForm.lot_no} onChange={(e) => setPForm({ ...pForm, lot_no: e.target.value })} /></div>
                    <div><Label>Expiry *</Label><Input type="date" value={pForm.expiry} onChange={(e) => setPForm({ ...pForm, expiry: e.target.value })} data-testid="purchase-expiry" /></div>
                    <div><Label>Quantity *</Label><Input type="number" min="1" value={pForm.quantity} onChange={(e) => setPForm({ ...pForm, quantity: e.target.value })} data-testid="purchase-qty" /></div>
                    <div><Label>Unit cost (₹)</Label><Input type="number" value={pForm.unit_cost} onChange={(e) => setPForm({ ...pForm, unit_cost: e.target.value })} /></div>
                    <div><Label>Supplier</Label><Input value={pForm.supplier} onChange={(e) => setPForm({ ...pForm, supplier: e.target.value })} /></div>
                    <div><Label>Invoice no</Label><Input value={pForm.invoice_no} onChange={(e) => setPForm({ ...pForm, invoice_no: e.target.value })} /></div>
                    <div className="md:col-span-2"><Label>Received date</Label><Input type="date" value={pForm.received_date} onChange={(e) => setPForm({ ...pForm, received_date: e.target.value })} placeholder="Defaults to today" /></div>
                  </div>
                  <Button onClick={savePurchase} className="mt-3 bg-brand hover:bg-brand-700" data-testid="purchase-save-btn">Save</Button>
                </DialogContent>
              </Dialog>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
                  <tr><th className="px-3 py-2 text-left">Received</th><th className="px-3 py-2 text-left">Implant</th><th className="px-3 py-2 text-left">Batch / Lot</th><th className="px-3 py-2 text-left">Expiry</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Unit ₹</th><th className="px-3 py-2 text-left">Supplier · Invoice</th></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {purchases.length === 0 && <tr><td colSpan="7" className="px-3 py-8 text-center text-ink-muted">No purchases recorded yet.</td></tr>}
                  {purchases.map((p) => (
                    <tr key={p.id} data-testid={`purchase-row-${p.id}`}>
                      <td className="px-3 py-2">{p.received_date}</td>
                      <td className="px-3 py-2 font-medium">{p.implant_name}</td>
                      <td className="px-3 py-2 font-mono text-xs">{p.batch_no}{p.lot_no ? ` / ${p.lot_no}` : ""}</td>
                      <td className="px-3 py-2">{p.expiry}</td>
                      <td className="px-3 py-2 text-right">{p.quantity}</td>
                      <td className="px-3 py-2 text-right">₹{(p.unit_cost || 0).toLocaleString("en-IN")}</td>
                      <td className="px-3 py-2 text-xs text-ink-muted">{p.supplier || "—"}{p.invoice_no ? ` · ${p.invoice_no}` : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* === Stock === */}
        <TabsContent value="stock" className="mt-4">
          <Card className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wider text-brand">Live stock — on-hand by batch</div>
              <div className="text-[11px] text-ink-muted">On-hand = total purchased − used in OT notes</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
                  <tr><th className="px-3 py-2 text-left">Implant</th><th className="px-3 py-2 text-left">Batch / Lot</th><th className="px-3 py-2 text-left">Expiry</th><th className="px-3 py-2 text-right">Purchased</th><th className="px-3 py-2 text-right">Used</th><th className="px-3 py-2 text-right">On-hand</th><th className="px-3 py-2 text-center">Status</th></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {stock.items.length === 0 && <tr><td colSpan="7" className="px-3 py-8 text-center text-ink-muted">No stock — record a purchase to get started.</td></tr>}
                  {stock.items.map((row) => {
                    const tone = STOCK_TONE[row.status] || STOCK_TONE.healthy;
                    return (
                      <tr key={`${row.implant_id}-${row.batch_no}`} className={`${row.status === "expired" || row.status === "out_of_stock" ? "bg-rose-50/40" : row.status === "expiring_soon" ? "bg-orange-50/40" : ""}`} data-testid={`stock-row-${row.implant_id}-${row.batch_no}`}>
                        <td className="px-3 py-2 font-medium">{row.implant_name}</td>
                        <td className="px-3 py-2 font-mono text-xs">{row.batch_no}{row.lot_no ? ` / ${row.lot_no}` : ""}</td>
                        <td className="px-3 py-2">{row.expiry}{row.days_to_expiry !== null && row.days_to_expiry < 60 && <span className={`ml-1 text-[10px] ${row.days_to_expiry < 0 ? "text-rose-700" : row.days_to_expiry <= 30 ? "text-amber-700" : "text-ink-muted"}`}>({row.days_to_expiry < 0 ? `expired ${Math.abs(row.days_to_expiry)}d ago` : `${row.days_to_expiry}d left`})</span>}</td>
                        <td className="px-3 py-2 text-right text-ink-muted">{row.qty_purchased}</td>
                        <td className="px-3 py-2 text-right text-ink-muted">{row.qty_used}</td>
                        <td className="px-3 py-2 text-right font-heading text-lg font-bold">{row.on_hand}</td>
                        <td className="px-3 py-2 text-center"><Badge className={`${tone.bg} ${tone.text} border-0`}>{tone.label}</Badge></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* === Report === */}
        <TabsContent value="report" className="mt-4">
          <div className="space-y-4">
            <Card className="p-4">
              <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-brand">Implants used (linked to OT)</div>
                  <div className="text-[11px] text-ink-muted">{report.summary?.used_count || 0} implants used · Total cost ₹{(report.summary?.used_cost_total || 0).toLocaleString("en-IN")}</div>
                </div>
                <div className="flex items-end gap-2">
                  <div><Label className="text-[10px]">From</Label><Input type="date" value={reportRange.from_date} onChange={(e) => setReportRange({ ...reportRange, from_date: e.target.value })} className="h-9" /></div>
                  <div><Label className="text-[10px]">To</Label><Input type="date" value={reportRange.to_date} onChange={(e) => setReportRange({ ...reportRange, to_date: e.target.value })} className="h-9" /></div>
                  <Button size="sm" variant="outline" onClick={runReport} data-testid="implant-report-run">Run</Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
                    <tr><th className="px-3 py-2 text-left">OT date</th><th className="px-3 py-2 text-left">Patient</th><th className="px-3 py-2 text-left">Surgery</th><th className="px-3 py-2 text-left">Surgeon</th><th className="px-3 py-2 text-left">Implant</th><th className="px-3 py-2 text-left">Batch · Expiry</th><th className="px-3 py-2 text-right">Cost ₹</th></tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {report.usage.length === 0 && <tr><td colSpan="7" className="px-3 py-8 text-center text-ink-muted">No implants used yet in OT.</td></tr>}
                    {report.usage.map((u, i) => (
                      <tr key={`${u.patient_uhid}-${u.ot_date}-${i}`} data-testid={`usage-row-${i}`}>
                        <td className="px-3 py-2">{u.ot_date}</td>
                        <td className="px-3 py-2"><div className="font-medium">{u.patient_name}</div><div className="text-[10px] font-mono text-ink-muted">{u.patient_uhid}</div></td>
                        <td className="px-3 py-2">{u.surgery}</td>
                        <td className="px-3 py-2 text-xs text-ink-muted">{u.surgeon}</td>
                        <td className="px-3 py-2 font-medium">{u.implant_name}</td>
                        <td className="px-3 py-2 font-mono text-xs">{u.batch_no} · {u.expiry}</td>
                        <td className="px-3 py-2 text-right">₹{(u.unit_cost || 0).toLocaleString("en-IN")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Implant purchase report — everything received in the selected
                date window with supplier, batch, expiry and total cost per
                line. Total units and spend are surfaced in the header. */}
            <Card className="p-4" data-testid="report-purchases-card">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-brand">Implants purchased</div>
                  <div className="mt-1 text-[11px] text-ink-muted">
                    {(report.summary?.purchased_units || 0)} unit{(report.summary?.purchased_units || 0) === 1 ? "" : "s"} received · Total cost ₹{(report.summary?.purchased_cost_total || 0).toLocaleString("en-IN")}
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
                    <tr>
                      <th className="px-3 py-2 text-left">Received</th>
                      <th className="px-3 py-2 text-left">Implant</th>
                      <th className="px-3 py-2 text-left">Manufacturer</th>
                      <th className="px-3 py-2 text-left">Supplier</th>
                      <th className="px-3 py-2 text-left">Invoice #</th>
                      <th className="px-3 py-2 text-left">Batch · Expiry</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2 text-right">Unit ₹</th>
                      <th className="px-3 py-2 text-right">Total ₹</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {(report.purchases || []).length === 0 && <tr><td colSpan="9" className="px-3 py-8 text-center text-ink-muted">No implant purchases in the selected range.</td></tr>}
                    {(report.purchases || []).map((p, i) => (
                      <tr key={`${p.batch_no}-${p.received_date}-${i}`} data-testid={`purchase-row-${i}`}>
                        <td className="px-3 py-2">{p.received_date}</td>
                        <td className="px-3 py-2">
                          <div className="font-medium">{p.implant_name}</div>
                          {p.lot_no && <div className="font-mono text-[10px] text-ink-muted">Lot {p.lot_no}</div>}
                        </td>
                        <td className="px-3 py-2 text-xs">{p.manufacturer || "—"}</td>
                        <td className="px-3 py-2 text-xs">{p.supplier || "—"}</td>
                        <td className="px-3 py-2 font-mono text-[11px]">{p.invoice_no || "—"}</td>
                        <td className="px-3 py-2 font-mono text-[11px]">{p.batch_no || "—"} · {p.expiry || "—"}</td>
                        <td className="px-3 py-2 text-right font-medium">{p.quantity}</td>
                        <td className="px-3 py-2 text-right">₹{p.unit_cost.toLocaleString("en-IN")}</td>
                        <td className="px-3 py-2 text-right font-semibold">₹{p.total_cost.toLocaleString("en-IN")}</td>
                      </tr>
                    ))}
                    {(report.purchases || []).length > 0 && (
                      <tr className="border-t-2 border-brand bg-brand-50 font-semibold">
                        <td className="px-3 py-2" colSpan="6">Total</td>
                        <td className="px-3 py-2 text-right">{report.summary?.purchased_units || 0}</td>
                        <td />
                        <td className="px-3 py-2 text-right text-brand">₹{(report.summary?.purchased_cost_total || 0).toLocaleString("en-IN")}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>

            <Card className="p-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wider text-brand">Pending stock alerts</div>
                <div className="flex gap-2 text-[11px]">
                  {report.summary?.out_of_stock > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 font-semibold text-rose-700"><AlertTriangle size={10} />{report.summary.out_of_stock} out of stock</span>}
                  {report.summary?.near_expiry > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 font-semibold text-orange-700"><AlertTriangle size={10} />{report.summary.near_expiry} near expiry</span>}
                  {(report.summary?.out_of_stock || 0) === 0 && (report.summary?.near_expiry || 0) === 0 && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-700"><CheckCircle2 size={10} />All healthy</span>}
                </div>
              </div>
              <div className="grid gap-2 md:grid-cols-2">
                {report.pending.filter((r) => r.status !== "healthy").slice(0, 12).map((r) => {
                  const tone = STOCK_TONE[r.status];
                  return (
                    <div key={`${r.implant_id}-${r.batch_no}`} className={`flex items-center justify-between rounded-md border border-border ${tone.bg} px-3 py-2`} data-testid={`pending-${r.implant_id}-${r.batch_no}`}>
                      <div>
                        <div className="font-medium">{r.implant_name}</div>
                        <div className="font-mono text-[10px] text-ink-muted">Batch {r.batch_no} · exp {r.expiry}</div>
                      </div>
                      <div className={`text-right ${tone.text}`}>
                        <div className="font-heading text-lg font-bold">{r.on_hand}</div>
                        <div className="text-[9px] uppercase tracking-wider">{tone.label}</div>
                      </div>
                    </div>
                  );
                })}
                {report.pending.filter((r) => r.status !== "healthy").length === 0 && <div className="col-span-2 py-6 text-center text-xs text-emerald-700">No alerts — all batches in healthy state.</div>}
              </div>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
